import React, { FC, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Box, Chip, IconButton, Switch, Tooltip, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import NotificationsIcon from '@mui/icons-material/Notifications';

import { ConfirmDialog, EditorDialog, showNotification, useDialogState } from 'components';
import {
  NotificationType,
  PermissionGroupDetailModel,
  PermissionGroupNotificationInputModel,
  PermissionGroupScopeInputModel,
} from 'models';
import Yup from 'utils/yup';
import { isAxiosError } from 'utils/errors';
import { useUpdatePermissionGroup } from 'features/permissionGroups';
import LeoTextField from 'components/input-fields/leoTextField/LeoTextField';

import PermissionGroupNotificationsDialog from './PermissionGroupNotificationsDialog';

export enum PermissionGroupErrors {
  NAME_ALREADY_EXISTS = 'PERMISSION_GROUP_NAME_ALREADY_EXISTS',
  LOCKOUT = 'PERMISSION_GROUP_LOCKOUT',
  NOT_FOUND = 'PERMISSION_GROUP_NOT_FOUND',
  UNKNOWN_USER = 'PERMISSION_GROUP_UNKNOWN_USER',
}

const KNOWN_PERMISSION_GROUP_ERRORS: string[] = [
  PermissionGroupErrors.LOCKOUT,
  PermissionGroupErrors.NAME_ALREADY_EXISTS,
  PermissionGroupErrors.NOT_FOUND,
  PermissionGroupErrors.UNKNOWN_USER,
];

/**
 * Maps a backend rejection code (from a permission-group mutation) to a translated
 * notification message, if it is one we know how to explain. Shared between the header
 * (rename/deactivate/notifications) and the members tab (bulk add/remove) so both surfaces
 * speak consistently about the same rejection codes.
 */
export const getPermissionGroupErrorMessage = (
  error: unknown,
  t: (key: string) => string
): string | null => {
  if (!isAxiosError(error)) {
    return null;
  }

  const code = error.response?.data?.message;

  if (typeof code === 'string' && KNOWN_PERMISSION_GROUP_ERRORS.includes(code)) {
    return t(`ERRORS.${code}`);
  }

  return null;
};

export const mapScopesToInput = (
  group: PermissionGroupDetailModel
): PermissionGroupScopeInputModel[] =>
  group.scopes.map((scope) => ({
    code: scope.scopeCode,
    view: scope.canView,
    edit: scope.canEdit,
  }));

// The PATCH endpoint replaces both `scopes` and `notifications` wholesale, so any mutation
// that isn't editing one of them (rename, activate/deactivate, saving the scopes tab) must
// still round-trip the group's current value here - otherwise it gets silently cleared.
export const mapNotificationsToInput = (
  group: PermissionGroupDetailModel
): PermissionGroupNotificationInputModel[] =>
  group.notifications.map((notification) => ({ type: notification.notificationType }));

export interface RenamePermissionGroupFormValues {
  name: string;
}

export interface PermissionGroupHeaderProps {
  group: PermissionGroupDetailModel;
  canEdit: boolean;
}

const validationSchema = Yup.object().shape({
  name: Yup.string().trim().required().max(255),
});

export const PermissionGroupHeader: FC<PermissionGroupHeaderProps> = ({ group, canEdit }) => {
  const { t } = useTranslation();
  const { mutateAsync: updatePermissionGroup } = useUpdatePermissionGroup();
  const [isTogglingActive, setIsTogglingActive] = useState(false);

  const {
    isOpen: isRenameOpen,
    openEmptyDialog: openRename,
    closeDialog: closeRename,
  } = useDialogState();

  const {
    isOpen: isDeactivateOpen,
    openEmptyDialog: openDeactivate,
    closeDialog: closeDeactivate,
  } = useDialogState();

  const {
    isOpen: isNotificationsOpen,
    openEmptyDialog: openNotifications,
    closeDialog: closeNotifications,
  } = useDialogState();

  const handleRenameSubmit = async (values: RenamePermissionGroupFormValues) => {
    try {
      await updatePermissionGroup({
        id: group.id,
        data: {
          name: values.name.trim(),
          isActive: group.isActive,
          scopes: mapScopesToInput(group),
          notifications: mapNotificationsToInput(group),
        },
      });
    } catch (error) {
      const message = getPermissionGroupErrorMessage(error, t);

      if (message) {
        showNotification({ content: message, type: NotificationType.ERROR });
      }

      throw error;
    }
  };

  const handleToggleActive = async () => {
    setIsTogglingActive(true);

    try {
      await updatePermissionGroup({
        id: group.id,
        data: {
          name: group.name,
          isActive: !group.isActive,
          scopes: mapScopesToInput(group),
          notifications: mapNotificationsToInput(group),
        },
      });
    } catch (error) {
      const message = getPermissionGroupErrorMessage(error, t);

      showNotification({
        content: message || t('ERRORS.NOT_FOUND'),
        type: NotificationType.ERROR,
      });
    } finally {
      setIsTogglingActive(false);
    }
  };

  return (
    <Box display="flex" alignItems="center">
      <Typography variant="h5" mr={1}>
        {group.name}
      </Typography>
      {canEdit && (
        <IconButton size="small" onClick={() => openRename()} aria-label="edit-name">
          <EditIcon fontSize="small" />
        </IconButton>
      )}

      <Tooltip title={<Trans i18nKey="KOZOS_JOGOSULTSAGI_CSOPORTOK.NOTIFICATIONS_DIALOG_TITLE" />}>
        <IconButton size="small" onClick={() => openNotifications()} aria-label="edit-notifications">
          <NotificationsIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Box display="flex" alignItems="center" ml={3}>
        <Chip
          size="small"
          color={group.isActive ? 'success' : 'default'}
          label={
            <Trans
              i18nKey={
                group.isActive
                  ? 'KOZOS_JOGOSULTSAGI_CSOPORTOK.ACTIVE'
                  : 'KOZOS_JOGOSULTSAGI_CSOPORTOK.INACTIVE'
              }
            />
          }
        />
        {canEdit && (
          <Switch
            checked={group.isActive}
            disabled={isTogglingActive}
            onChange={() => openDeactivate()}
            inputProps={{ 'aria-label': 'toggle-active' }}
          />
        )}
      </Box>

      {canEdit && (
        <EditorDialog<RenamePermissionGroupFormValues>
          initialValues={{ name: group.name }}
          maxWidth="sm"
          onSubmit={handleRenameSubmit}
          onClose={closeRename}
          open={isRenameOpen}
          validationSchema={validationSchema}
          title={<Trans i18nKey="KOZOS_JOGOSULTSAGI_CSOPORTOK.RENAME_DIALOG_TITLE" />}
          submitText="KOZOS_JOGOSULTSAGI_CSOPORTOK.RENAME_DIALOG_SUBMIT"
        >
          <LeoTextField
            name="name"
            maxLength={255}
            label={<Trans i18nKey="KOZOS_JOGOSULTSAGI_CSOPORTOK.NAME" />}
          />
        </EditorDialog>
      )}

      {canEdit && (
        <ConfirmDialog
          open={isDeactivateOpen}
          onClose={closeDeactivate}
          onSubmit={handleToggleActive}
          title={
            <Trans
              i18nKey={
                group.isActive
                  ? 'KOZOS_JOGOSULTSAGI_CSOPORTOK.DEACTIVATE_DIALOG_TITLE'
                  : 'KOZOS_JOGOSULTSAGI_CSOPORTOK.ACTIVATE_DIALOG_TITLE'
              }
            />
          }
          confirmTitle={<Trans i18nKey="COMMON.OK" />}
          cancelTitle={<Trans i18nKey="COMMON.CANCEL" />}
        >
          <Trans
            i18nKey={
              group.isActive
                ? 'KOZOS_JOGOSULTSAGI_CSOPORTOK.DEACTIVATE_DIALOG_BODY'
                : 'KOZOS_JOGOSULTSAGI_CSOPORTOK.ACTIVATE_DIALOG_BODY'
            }
          />
        </ConfirmDialog>
      )}

      <PermissionGroupNotificationsDialog
        group={group}
        open={isNotificationsOpen}
        onClose={closeNotifications}
        canEdit={canEdit}
      />
    </Box>
  );
};

export default PermissionGroupHeader;
